import { GenericColors } from 'api/contract'
import { createIcon } from 'components/icons/IconFactory'
import React, { useMemo, ReactNode } from 'react'

interface GenericBadgeProps {
  label?: string
  iconKey?: string
  icon?: ReactNode
  color: GenericColors
  badgeType: 'status' | 'sm-bold' | 'round'
  className?: string
}

const Badge: React.FC<GenericBadgeProps> = ({ label, color, badgeType, className, iconKey, icon }) => {
  const getColorClasses = (color: GenericColors) => {
    switch (color) {
      case GenericColors.Green:
        return 'bg-green-50 text-green-500 ring-green-600/20'
      case GenericColors.Yellow:
        return 'bg-amber-50 text-amber-600 ring-amber-600/20'
      case GenericColors.Red:
        return 'bg-rose-50 text-red-500 ring-red-600/20'
      case GenericColors.Primary:
        return 'bg-primary/10 text-primary ring-primary/20'
      case GenericColors.Secondary:
        return 'bg-secondary/10 text-secondary ring-secondary/20'
      case GenericColors.Gray:
        return 'bg-gray-50 text-gray-600 ring-gray-600/20'
      case GenericColors.Blue:
        return 'bg-blue-50 text-blue-500 ring-blue-600/20'
      case GenericColors.Orange:
        return 'bg-orange-50 text-orange-500 ring-orange-600/20'
      case GenericColors.Purple:
        return 'bg-purple-50 text-purple-500 ring-purple-600/20'
      case GenericColors.Pink:
        return 'bg-pink-50 text-pink-500 ring-pink-600/20'
      default:
        return 'bg-zinc-500 text-white'
    }
  }

  const badgeStyle = useMemo(() => {
    if (badgeType === 'round') {
      return 'inline-flex items-center rounded-full p-2 text-xs font-bold'
    }
    if (badgeType === 'sm-bold') {
      return 'inline-flex items-center rounded-md px-2 py-1 text-xs font-bold ring-1 ring-inset'
    }
    return 'inline-flex text-center justify-center items-center rounded-full px-6 py-2 text-sm font-bold'
  }, [badgeType])

  const renderIcon = useMemo(() => {
    if (icon) {
      return icon
    }
    if (iconKey) {
      const Icon = createIcon(iconKey)
      return <Icon className="w-5" />
    }
    return null
  }, [icon, iconKey])

  return (
    <span
      className={`${badgeStyle} ${getColorClasses(color)} ${className || ''}`}
    >
      {renderIcon}
      {label && <span className={renderIcon ? 'ml-1' : ''}>{label}</span>}
    </span>
  )
}

export default Badge
